import { View, Text, Image } from '@react-pdf/renderer';
import { LaudoHeader, Footer } from '../../../types';
import { avmPageStyles as s } from './styles';

// ─── Logo (SVG-like shapes via react-pdf) ────────────────────────────
interface AVMLogoProps {
  logoBase64?: string;
}

export function AVMLogo({ logoBase64 }: AVMLogoProps) {
  if (logoBase64) {
    return (
      <Image
        src={logoBase64}
        style={{ width: 64, height: 24, objectFit: 'contain' }}
      />
    );
  }
  return (
    <Text style={{ fontSize: 16, fontWeight: 'bold', color: '#101010', letterSpacing: 1 }}>
      bari.
    </Text>
  );
}

// ─── Page Header Bar ─────────────────────────────────────────────────
interface AVMPageHeaderProps {
  header: LaudoHeader;
  logoBase64?: string;
}

export function AVMPageHeader({ header, logoBase64 }: AVMPageHeaderProps) {
  return (
    <View style={s.headerBar}>
      <AVMLogo logoBase64={logoBase64} />
      <View style={{ flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
        <Text style={s.headerTitle}>LAUDO DE AVALIAÇÃO</Text>
        <Text style={s.headerSubtitle}>{header.idLaudo}  ·  {header.formatoLaudo}</Text>
      </View>
    </View>
  );
}

// ─── Section Header (light background) ──────────────────────────────
interface AVMSectionHeaderProps {
  title: string;
}

export function AVMSectionHeader({ title }: AVMSectionHeaderProps) {
  return (
    <View style={s.sectionHeader}>
      <View style={s.sectionHeaderAccent} />
      <Text style={s.sectionHeaderText}>{title}</Text>
    </View>
  );
}

// ─── Section Header Dark (for amostras) ─────────────────────────────
interface AVMSectionHeaderDarkProps {
  title: string;
  value?: string;
}

export function AVMSectionHeaderDark({ title, value }: AVMSectionHeaderDarkProps) {
  return (
    <View style={s.sectionHeaderDark}>
      <View style={s.sectionHeaderAccent} />
      <Text style={s.sectionHeaderDarkText}>{title}</Text>
      {value && <Text style={s.sectionHeaderDarkValue}>{value}</Text>}
    </View>
  );
}

// ─── Data Field (label + value) ─────────────────────────────────────
interface AVMFieldProps {
  label: string;
  value: string | number;
  flex?: number;
  width?: number;
}

export function AVMField({ label, value, flex, width }: AVMFieldProps) {
  const safeValue = value === '' || value === undefined || value === null ? '-' : value;
  const style: Record<string, any> = { flexDirection: 'column' as const, gap: 2 };
  if (width) style.width = width;
  else if (flex !== undefined) style.flex = flex;
  else style.flex = 1;

  return (
    <View style={style}>
      <Text style={s.fieldLabel}>{label}</Text>
      <Text style={s.fieldValue}>{safeValue}</Text>
    </View>
  );
}

// ─── Page Footer ────────────────────────────────────────────────────
interface AVMPageFooterProps {
  footer: Footer;
}

export function AVMPageFooter({ footer }: AVMPageFooterProps) {
  return (
    <View
      fixed
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: 28,
        paddingHorizontal: 32,
        backgroundColor: '#F5F6F9',
        borderTop: '1 solid #D5DBE7',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <Text style={s.footerText}>v{footer.versaoDocumento}</Text>
      <Text style={s.footerText}>powered by {footer.poweredBy}</Text>
      <Text
        style={s.footerTextBold}
        render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
      />
    </View>
  );
}
